<template>
  <v-row class="import-job-detail">
    <v-col cols="12">
      <div class="d-flex align-center mb-4">
        <v-btn icon class="mr-2" @click="goBack">
          <v-icon>{{ icons.back }}</v-icon>
        </v-btn>
        <h2 class="text-h5 font-weight-medium">Recipe Import Job Details</h2>
      </div>
    </v-col>

    <!-- Loading State -->
    <v-col cols="12" v-if="loading && !job">
      <v-card outlined class="pa-8 text-center">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-4 grey--text">Loading job details...</p>
      </v-card>
    </v-col>

    <!-- Job Details -->
    <template v-if="job">
      <v-col cols="12">
        <v-card outlined class="pa-4">
          <div class="d-flex justify-space-between align-center">
            <v-card-title class="px-0 pt-0">Job Information</v-card-title>
            <div v-if="canRetry">
              <v-btn
                small
                color="primary"
                :loading="retrying"
                :disabled="retrying"
                @click="retry"
              >
                <v-icon small class="mr-1">{{ icons.refresh }}</v-icon>
                Retry
              </v-btn>
            </div>
          </div>
          <v-divider class="mb-4" />

          <v-row>
            <v-col cols="12" md="6">
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Status:</span
                >
                <v-chip :color="getStatusColor(job.status)" small dark>
                  {{ capitalize(job.status) }}
                </v-chip>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Google Sheet ID:</span
                >
                <a
                  :href="
                    `https://docs.google.com/spreadsheets/d/${job.source_google_sheet_id}`
                  "
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ job.source_google_sheet_id }}
                </a>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Created At:</span
                >
                <span>{{ formatDate(job.created_at) }}</span>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Completed At:</span
                >
                <span>{{
                  job.completed_at ? formatDate(job.completed_at) : '-'
                }}</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 200px"
                  >Total Recipes:</span
                >
                <strong>{{ job.total_rows }}</strong>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 200px"
                  >Recipes Imported:</span
                >
                <strong class="success--text">{{ job.success_count }}</strong>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 200px"
                  >Skipped (already existed):</span
                >
                <strong>{{ skippedCount }}</strong>
              </div>
            </v-col>
          </v-row>

          <!-- Config Options -->
          <div v-if="hasConfigOptions" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2"
              >Import Options:</span
            >
            <v-chip
              v-for="(name, i) in selectedCategoryNames"
              :key="`cat-${i}`"
              small
              class="mr-2 mb-2"
              color="blue"
              dark
            >
              {{ name }}
            </v-chip>
            <v-chip
              v-if="job.config && job.config.reset_ingredients"
              small
              class="mr-2 mb-2"
              color="orange"
              dark
            >
              Reset ingredients
            </v-chip>
            <v-chip
              v-if="job.config && job.config.reset_booster_directions"
              small
              class="mr-2 mb-2"
              color="orange"
              dark
            >
              Reset boosters &amp; directions
            </v-chip>
            <v-chip
              v-if="job.config && job.config.use_category_column === false"
              small
              class="mr-2 mb-2"
              color="grey"
              dark
            >
              Category column ignored
            </v-chip>
          </div>

          <!-- Progress Bar -->
          <div v-if="job.total_rows" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2"
              >Progress:</span
            >
            <v-progress-linear
              :value="getProgressPercentage(job)"
              :color="getProgressColor(job)"
              height="24"
              rounded
            >
              <template v-slot:default>
                <span class="white--text font-weight-medium">
                  {{ job.success_count }} / {{ job.total_rows }} recipes ({{
                    Math.round(getProgressPercentage(job))
                  }}%)
                </span>
              </template>
            </v-progress-linear>
          </div>

          <!-- Logs -->
          <div v-if="job.logs" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2">Logs:</span>
            <v-card outlined class="pa-3 logs-container" color="grey lighten-4">
              <pre class="text-body-2 mb-0" style="white-space: pre-wrap">{{
                job.logs
              }}</pre>
            </v-card>
          </div>
        </v-card>
      </v-col>
    </template>

    <!-- Toast Notification -->
    <toast ref="toast" />
  </v-row>
</template>

<script>
import { mdiArrowLeft, mdiRefresh } from '@mdi/js'
import moment from 'moment'
import Toast from '@/components/elements/Toast'

export default {
  name: 'RecipeImportJobDetailPage',

  components: {
    Toast,
  },

  data() {
    return {
      loading: false,
      job: null,
      categories: [],
      autoRefreshTimer: null,
      retrying: false,
      icons: {
        back: mdiArrowLeft,
        refresh: mdiRefresh,
      },
    }
  },

  computed: {
    jobId() {
      return this.$route.params.id
    },

    canRetry() {
      return this.job && !['pending', 'processing'].includes(this.job.status)
    },

    skippedCount() {
      return this.job?.config?.skipped_count || 0
    },

    selectedCategoryNames() {
      const ids = this.job?.config?.food_category_ids || []
      return ids.map((id) => {
        const match = this.categories.find((category) => category.id === id)
        return match ? match.name : `Category #${id}`
      })
    },

    hasConfigOptions() {
      if (!this.job || !this.job.config) return false
      const config = this.job.config
      return !!(
        (config.food_category_ids && config.food_category_ids.length) ||
        config.reset_ingredients ||
        config.reset_booster_directions
      )
    },
  },

  created() {
    this.fetchJobDetail()
    this.fetchCategories()
  },

  beforeDestroy() {
    this.stopAutoRefresh()
  },

  methods: {
    goBack() {
      this.$router.push({ name: 'import.recipe' })
    },

    async fetchCategories() {
      try {
        await this.$store.dispatch('foodCategory/getDataList', {})
        this.categories = this.$store.state.foodCategory.list
      } catch (error) {
        this.categories = []
      }
    },

    async fetchJobDetail() {
      if (!this.jobId) {
        this.$refs.toast?.open({
          color: 'error',
          message: 'Job ID is required',
        })
        return
      }

      this.loading = true
      try {
        const { data } = await this.$api.get(`foods/import-jobs/${this.jobId}`)
        this.job = data.data || data

        // Start or stop auto-refresh based on job status
        if (this.shouldAutoRefresh()) {
          this.startAutoRefresh()
        } else {
          this.stopAutoRefresh()
        }
      } catch (error) {
        this.$refs.toast?.open({
          color: 'error',
          message:
            error.response?.data?.message || 'Failed to fetch job details',
        })
      } finally {
        this.loading = false
      }
    },

    shouldAutoRefresh() {
      return this.job && ['pending', 'processing'].includes(this.job.status)
    },

    startAutoRefresh() {
      if (this.autoRefreshTimer) return
      this.autoRefreshTimer = setInterval(() => {
        this.refreshJobStatus()
      }, 3000)
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },

    async refreshJobStatus() {
      try {
        const { data } = await this.$api.get(`foods/import-jobs/${this.jobId}`)
        this.job = data.data || data

        // Stop auto-refresh if job is no longer pending or processing
        if (!this.shouldAutoRefresh()) {
          this.stopAutoRefresh()
        }
      } catch (error) {
        // Silently fail on auto-refresh errors
      }
    },

    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red',
        cancelled: 'grey',
      }
      return colors[status] || 'grey'
    },

    getProgressPercentage(job) {
      if (!job.total_rows) return 0
      return (job.success_count / job.total_rows) * 100
    },

    getProgressColor(job) {
      if (job.status === 'completed') {
        return 'success'
      }
      if (job.status === 'failed') {
        return 'error'
      }
      return 'primary'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('MMM DD, YYYY HH:mm')
    },

    async retry() {
      this.retrying = true
      try {
        await this.$api.post(`foods/import-jobs/${this.jobId}/rerun`)
        this.$refs.toast?.open({
          color: 'success',
          message: 'Retry initiated successfully',
        })
        // Refresh job details
        await this.fetchJobDetail()
      } catch (error) {
        this.$refs.toast?.open({
          color: 'error',
          message: error.response?.data?.message || 'Failed to retry the job',
        })
      } finally {
        this.retrying = false
      }
    },
  },
}
</script>

<style scoped>
.import-job-detail h2 {
  font-size: 1.5rem;
  font-weight: 500;
}

pre {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.logs-container {
  max-height: 200px;
  overflow-y: auto;
}
</style>
